import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo } from 'react';

import { Empty } from '@/components/UI/Empty';

import MarketingHeaderSlot from '@/components/shared/MarketingHeaderSlot';
import Footer from '@/renderer/Footer';
import { useDebounce } from '@/renderer/utils/hooks/useDebounce';
import { usePageContext } from '@/renderer/utils/usePageContext';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

import {
  activatePreviewMode,
  deactivatePreviewMode,
  isPreviewModeActive,
} from '@/renderer/utils/previewMode';
import { mixpanelTrack } from '@/renderer/utils/stat/mixp';
import {
  BettingTalkIcon,
  CBBIcon,
  CFBIconSolid,
  NFLIconSolid,
  RASPlusIcon,
} from './components/ProductIcons';
import ProductMenu from './components/ProductMenu';
import ProductPage, { ProductPageSkeleton } from './components/ProductPage';
import SportCards from './components/SportCards';
import { sportIdFromName } from './components/productHelpers';

export const getProducts: RequestSchema<
  API.MembershipProduct<true>[], // actually API.WithPage but useRequest only returns the `data` field
  API.PageInput & API.WithData<{ ids?: number[] }>
> = {
  input: '/api/v1/product',
};

const OrderPage: React.FC<{ tag?: string }> = ({ tag: givenTag }) => {
  const pageContext = usePageContext();
  const tag = pageContext.routeParams.tag || givenTag;

  const urlPreviewParam = pageContext.urlParsed?.search?.preview;
  const urlPreview = urlPreviewParam === '1';
  // ?preview=1 turns preview mode on (persisted via cookie); ?preview=0 turns it off.
  useEffect(() => {
    if (urlPreviewParam === '1') {
      activatePreviewMode();
    } else if (urlPreviewParam === '0') {
      deactivatePreviewMode();
    }
  }, [urlPreviewParam]);
  const preview = urlPreview || (urlPreviewParam !== '0' && isPreviewModeActive()) ? 1 : undefined;

  const opts = useMemo(
    () => ({
      skipRefresh: true,
      query: {
        preview,
        data: {
          id: pageContext.routeParams.sportId ? undefined : pageContext.routeParams.productId,
          sportId: sportIdFromName(pageContext.routeParams.sportId),
          'tags$@>': tag && [tag],
        },
        page: {
          limit: 100,
        },
      },
    }),
    [pageContext.routeParams.productId, pageContext.routeParams.sportId, tag, preview],
  );
  const productsRequest = useRequest(getProducts, opts);
  const isLoading = useDebounce(productsRequest.loading, 300);

  // Unfiltered request — used only by ProductMenu to know which sport tabs to show.
  // Must not be filtered by sport/product so all active sports are always visible.
  const allProductsRequest = useRequest(
    getProducts,
    useMemo(
      () => ({
        skipRefresh: true,
        query: { preview, page: { limit: 100 } },
      }),
      [preview],
    ),
  );

  const products = productsRequest.data;
  const error = productsRequest.error;

  const product = useMemo(() => {
    if (products) {
      if (pageContext.routeParams.productId) {
        return (
          products.find((prod) => `${prod.id}` === `${pageContext.routeParams.productId}`) ||
          undefined
        );
      }

      if (pageContext.routeParams.sportId) {
        let best: API.MembershipProduct<true> | undefined = undefined;
        const sportId = sportIdFromName(pageContext.routeParams.sportId);

        if (typeof sportId === 'number') {
          for (const prod of products) {
            if (prod.sportIds?.includes(sportId)) {
              if (!best) {
                best = prod;
              }

              if (prod.sportIds.length === 1) {
                best = prod;
                break;
              }
            }
          }

          return best || undefined;
        }
      }

      if (tag) {
        let best: API.MembershipProduct<true> | undefined = undefined;

        for (const prod of products) {
          if (prod.tags?.includes(tag)) {
            if (!best) {
              best = prod;
            }

            if (prod.tags.length === 1) {
              best = prod;
              break;
            }
          }
        }

        return best || undefined;
      }
    }

    return undefined;
  }, [products, pageContext.routeParams.productId, pageContext.routeParams.sportId, tag]);

  const routeParams = pageContext.routeParams;
  const isAllProductsPage = !routeParams?.tag && !routeParams?.sportId && !routeParams?.productId;
  const singleProduct = isAllProductsPage && products?.length === 1 ? products[0] : undefined;

  const activeProduct = product || singleProduct;

  const IconComponent = useMemo(() => {
    switch (sportIdFromName(pageContext.routeParams.sportId) || activeProduct?.sportIds?.[0]) {
      case 1:
        return CFBIconSolid;

      case 2:
        return CBBIcon;

      case 3:
        return NFLIconSolid;
    }

    if (activeProduct?.tags?.includes('ras-plus')) {
      return RASPlusIcon;
    }

    if (activeProduct?.tags?.includes('betting-talk')) {
      return BettingTalkIcon;
    }

    // @todo better default icon?
    return CFBIconSolid;
  }, [pageContext.routeParams.sportId, activeProduct?.sportIds, activeProduct?.tags]);

  useEffect(() => {
    if (product?.id) {
      mixpanelTrack('order/client:view', { product_id: product?.id });
    }
  }, [product]);

  return (
    <div className="overflow-x-hidden bg-zinc-50 dark:bg-zinc-900">
      <MarketingHeaderSlot />
      <ProductMenu
        products={products}
        allProducts={allProductsRequest.data}
        product={product}
        isLoading={isLoading}
        routeParams={pageContext.routeParams}
      />
      <div>
        <div className="mx-auto max-w-5xl px-4 py-6 2xl:px-0">
          {pageContext.urlParsed?.search?.source && (
            <div className="mb-8 flex flex-row items-center justify-start space-x-2 rounded-sm bg-amber-50 p-4 text-amber-700">
              <LockClosedIcon height={24} className="size-6 shrink-0" />
              <span className="font-medium text-sm sm:text-base">
                You must be a Betting Talk member to view that thread. Join below.
              </span>
            </div>
          )}
          {/* Empty — only once the fetch has completed and there are genuinely no products
              (products is a loaded, empty array). While products is still undefined/loading we
              render skeletons below instead, so the empty state never flashes. */}
          {!productsRequest.loading &&
            products &&
            products.length === 0 &&
            (error ? (
              <Empty
                IconComponent={IconComponent}
                title="We ran into an error fetching our products. Please try again later."
                description={`Error message: ${error.message}.`}
              />
            ) : (
              <Empty
                IconComponent={IconComponent}
                title="No Products Found"
                description="We're sorry, we aren't currently offering any products that match your search."
              />
            ))}

          {/* Content, or a skeleton while products are still loading */}
          {(!products || productsRequest.loading || products.length > 0) &&
            (isAllProductsPage && !singleProduct ? (
              <SportCards products={products} isLoading={isLoading} />
            ) : product || singleProduct ? (
              <ProductPage
                product={(product || singleProduct)!}
                IconComponent={IconComponent}
                isLoading={isLoading}
              />
            ) : productsRequest.loading || !products ? (
              <ProductPageSkeleton />
            ) : null)}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OrderPage;
